import React, { useState } from "react";
import "./PropertyCard.css";
import {
  Hash,
  MapPinned,
  Bed,
  Bath,
  Car,
  StretchHorizontal,
  LandPlot,
  BadgeCheck,
} from "lucide-react";
import SoldImg from "../media/sold.png";
// 1. Accept the new 'onViewDetails' prop here
const PropertyCard = ({ property, onViewDetails }) => {
  const {
    images,
    address,
    suburb,
    lot, // Unused
    availability, // Unused
    status: dbStatus,
    registrationConstructionStatus,
    price,
    frontage,
    landSize,
    buildSize, // Unused
    bedrooms,
    bathrooms,
    parking,
    propertyType,
    priceCustomerVisibility,
  } = property;

  const displayStatus = (() => {
    const avail = availability?.toLowerCase().trim();

    if (avail === "sold") {
      return "SOLD";
    }

    // If availability is anything else (e.g., 'Available', 'New Listing', or empty),
    // we display 'FOR SALE' (or 'Available'). Use 'FOR SALE' for clarity.
    return "FOR SALE";
  })();

  // Format price with AUD currency
  const formatPrice = (priceStr) => {
    if (!priceStr || priceStr === "Price on request") return priceStr;

    // Extract numbers from price string
    const numbers = priceStr.toString().replace(/[^0-9]/g, "");
    const numPrice = parseInt(numbers);

    if (isNaN(numPrice)) return priceStr;

    // Format as AUD currency
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numPrice);
  };

  // Separate images and PDFs
  const cardImages = images?.filter(img => !img.toLowerCase().split('?')[0].endsWith('.pdf')) || [];
  const cardPdfs = images?.filter(img => img.toLowerCase().split('?')[0].endsWith('.pdf')) || [];
  const hasRealImages = cardImages.length > 0;
  const hasPdf = cardPdfs.length > 0;
  const [imageError, setImageError] = useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <div className="property-card">
      {/* Image Section */}
      <div className="card-image">
        {property.availability?.toLowerCase().trim() === "sold" && (
          <div className="sold-badge">
            {" "}
            <img src={SoldImg} alt="Sold" />
          </div>
        )}
        {hasRealImages && !imageError ? (
          <img
            src={cardImages[0]}
            alt={`${address}, ${suburb}`}
            loading="lazy"
            decoding="async"
            onError={handleImageError}
          />
        ) : hasPdf ? (
          <iframe
            src={`${cardPdfs[0]}#toolbar=0&navpanes=0&scrollbar=0&view=FitH&page=1`}
            title="Property PDF Thumbnail"
            scrolling="no"
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              overflow: "hidden",
              pointerEvents: "none", // Prevent interaction on card
            }}
          />
        ) : propertyType &&
          propertyType.trim().toLowerCase().includes("land") &&
          !propertyType.trim().toLowerCase().includes("home") ? (
          <div className="image-placeholder land-placeholder">
            <div className="placeholder-icon">
              <LandPlot size={48} color="#dc2626" />
              <p>Land for Sale</p>
            </div>
          </div>
        ) : (
          <div className="image-placeholder">
            <div className="placeholder-icon">
              <svg
                width="40"
                height="80"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 7V5C3 3.89543 3.89543 3 5 3H7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7 21H5C3.89543 21 3 20.1046 3 19V17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M21 17V19C21 20.1046 20.1046 21 19 21H17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17 3H19C20.1046 3 21 3.89543 21 5V7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        )}
        <div className="image-overlay">
          {/* 2. Add the onClick handler to the button */}
          <button className="view-btn" onClick={onViewDetails}>
            View Details
          </button>
        </div>
      </div>

      {/* Content Section */}
      <div className="card-content">
        {/* Simplified horizontal layout */}
        <div className="property-info-horizontal">
          <div className="card-footer">
            <div className="property-type">
              {propertyType &&
                propertyType.trim().toLowerCase().includes("land") &&
                !propertyType.trim().toLowerCase().includes("home")
                ? "Land Only"
                : (propertyType === "  Home and Land Packages"
                  ? "  Home & Land"
                  : propertyType
                ).trim()}
            </div>
            {(priceCustomerVisibility === "1" ||
              (propertyType &&
                propertyType.trim().toLowerCase().includes("land") &&
                !propertyType.trim().toLowerCase().includes("home"))) && (
                <span className="detail-item price">{formatPrice(price)}</span>
              )}
          </div>

          <div className="property-location">
            <p className="property-address">
              <MapPinned size={18} color="#b91c1c" />
              {property.lot ? `Lot ${property.lot}, ` : ""}{address},{suburb}
            </p>
          </div>

          <hr />
          <div className="property-details-card">
            {landSize > 0 && (
              <div className="detail-item">
                <LandPlot
                  color="#b91c1c"
                  style={{ width: "20px", height: "20px" }}
                />
                {landSize} sqm
              </div>
            )}
            {propertyType &&
              propertyType.trim().toLowerCase().includes("land") &&
              !propertyType.trim().toLowerCase().includes("home") &&
              registrationConstructionStatus && (
                <div className="detail-item">
                  <BadgeCheck
                    color="#b91c1c"
                    style={{ width: "20px", height: "20px" }}
                  />
                  {registrationConstructionStatus}
                </div>
              )}
            {propertyType &&
              propertyType.trim().toLowerCase().includes("land") &&
              !propertyType.trim().toLowerCase().includes("home") ? (
              <>
                {frontage > 0 && (
                  <div className="detail-item">
                    <StretchHorizontal
                      color="#b91c1c"
                      style={{ width: "20px", height: "20px" }}
                    />
                    {frontage}m Front
                  </div>
                )}
              </>
            ) : (
              <>
                {bedrooms > 0 && (
                  <div className="detail-item">
                    <Bed
                      color="#b91c1c"
                      style={{ width: "20px", height: "20px" }}
                    />
                    {bedrooms}
                  </div>
                )}
                {bathrooms > 0 && (
                  <div className="detail-item">
                    <Bath
                      color="#b91c1c"
                      style={{ width: "20px", height: "20px" }}
                    />
                    {bathrooms}
                  </div>
                )}
                {parking > 0 && (
                  <div className="detail-item">
                    <Car
                      color="#b91c1c"
                      style={{ width: "20px", height: "20px" }}
                    />
                    {parking}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
      </div>
    </div>
  );
};

export default PropertyCard;
